import {
  getIconText,
  getProgressBarColor,
  getStatusIcon,
} from "components/main-view/utils/functions/progressUtils";
import useMouseClickOutside from "hooks/MouseClickOutside";
import React, { useRef, useState } from "react";
import { MdMoreHoriz } from "react-icons/md";
import { StudentListType } from "utils/interfaces";
import EditDeleteMenu from "../components/EditDeleteMenu";
import { formatLastAccessDate } from "utils/formatDate";

interface StudentCardProps {
  student: StudentListType;
  handleCardClick: (event: React.MouseEvent) => void;
  handleDelete: (event: React.MouseEvent) => void;
}

const StudentCard: React.FC<StudentCardProps> = ({
  student,
  handleCardClick,
  handleDelete,
}) => {
  // States definition
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const includeEdit = student.canEdit || false;
  const includeDelete = student.canDelete || false;
  const activateEditButton = includeEdit || includeDelete;

  // Create a menu ref
  const menuRef: React.RefObject<HTMLDivElement> = useRef(null);

  // Define the class name based on the student data
  const className =
    student && student.classes && student.classes.length > 0
      ? student.classes[0].name
      : "-";

  // Define the last access date
  const lastAccess =
    student && student.lastActiveAt
      ? formatLastAccessDate(student.lastActiveAt)
      : null;

  // Getting the progress and aptitude
  const progress = student && student.progressRate ? student.progressRate : 0;
  const aptitude = student && student.aptitudeRate ? student.aptitudeRate : 0;

  /**
   * Hook used when clicking outside the dropdown menu
   */
  useMouseClickOutside(
    menuRef,
    () => setShowOptionsMenu(false),
    showOptionsMenu
  );

  /**
   * Function used to open the pop up menu with the edit and
   * delete option
   * @param event
   */
  const handleClickOptions = (event: React.MouseEvent) => {
    // First of all, prevent clicking the card
    event.stopPropagation();

    // Toggle the state
    setShowOptionsMenu(!showOptionsMenu);
  };

  /**
   * Function used to wrap the edit or delete functions
   * in order to add custom functionalities per card
   */
  const handleClickOptionWrapper = (
    func: (event: React.MouseEvent) => void
  ) => {
    return (event: React.MouseEvent) => {
      // Close the menu when clicking on one of the options
      setShowOptionsMenu(false);

      // And execute the function
      func(event);
    };
  };

  return (
    <div className="w-full h-fit px-6 py-4 bg-neutral-25 rounded-xl justify-between items-center inline-flex">
      <div className="w-[190px] self-stretch flex-col justify-center items-start inline-flex gap-1.5">
        <div className="self-stretch text-neutral-700 text-lg font-semibold font-sans leading-[21px] overflow-hidden break-keep whitespace-nowrap text-ellipsis">
          {`${student.firstName} ${student.lastName}` || "-"}
        </div>
        <div className="self-stretch text-start text-neutral-500 text-sm font-normal font-sans leading-[17px] text-ellipsis overflow-hidden text-nowrap break-keep whitespace-nowrap">
          {student.username || "-"}
        </div>
        <div className="max-w-[190px] h-fit px-2.5 py-1.5 bg-neutral-100 rounded-xl inline-flex justify-center items-center gap-2">
          <div className="text-center text-neutral-500 justify-start text-xs font-normal font-sans leading-[12px] text-ellipsis text-nowrap break-keep whitespace-nowrap overflow-hidden">
            {className}
          </div>
        </div>
      </div>
      <div className="flex-col justify-center items-center gap-1 inline-flex">
        <div className="text-center text-neutral-500 text-lg font-semibold font-sans leading-snug">
          {progress}%
        </div>
        <div className="text-center text-neutral-500 text-xs font-normal font-inter uppercase leading-[14px] tracking-wide">
          {/* Course Completed */}
          Progress
        </div>
        <div className="self-stretch h-1.5 bg-neutral-200 rounded-sm flex-col justify-between items-start flex">
          <div
            className={`h-1.5 ${getProgressBarColor(
              progress
            )} rounded-tl-sm rounded-bl-sm`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
      <div className="flex-col justify-center items-center gap-1 inline-flex">
        <div className="text-center text-neutral-500 text-lg font-semibold font-sans leading-snug">
          {aptitude}%
        </div>
        <div className="text-center text-neutral-500 text-xs font-normal font-inter uppercase leading-[14px] tracking-wide">
          {/* Avg. Quiz Score */}
          Aptitude
        </div>
        <div className="self-stretch h-1.5 bg-neutral-200 rounded-sm flex-col justify-between items-start flex">
          <div
            className={`h-1.5 ${getProgressBarColor(
              aptitude
            )} rounded-tl-sm rounded-bl-sm`}
            style={{ width: `${aptitude}%` }}
          />
        </div>
      </div>
      <div className="w-[150px] inline-flex flex-col justify-center items-center gap-1">
        {lastAccess ? (
          <div className="text-center justify-start text-neutral-500 text-sm font-normal font-sans leading-none">
            {lastAccess}
          </div>
        ) : (
          <div className="px-1.5 py-0.5 bg-secondary-600 opacity-70 rounded flex justify-center items-center gap-2">
            <div className="text-center justify-start text-white text-[10px] font-semibold font-sans uppercase leading-3 tracking-wide select-none">
              Invite sent
            </div>
          </div>
        )}
        <div className="text-center justify-start text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
          Last accessed
        </div>
      </div>
      <div className="w-[45px] self-stretch justify-center items-center flex">
        <div className="flex-col justify-start items-center gap-1 inline-flex">
          <div className="w-8 h-8 relative">{getStatusIcon(progress || 0)}</div>
          <div className="justify-center items-center gap-2 inline-flex">
            <div className="text-neutral-500 text-xs font-semibold font-sans leading-[14px]">
              {getIconText(progress || 0)}
            </div>
          </div>
        </div>
      </div>
      <div
        className="self-stretch justify-center items-center gap-3 inline-flex relative"
        ref={menuRef}
      >
        <button
          className={`p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex ${
            activateEditButton
              ? "cursor-pointer hover:bg-neutral-200 ease-in-out duration-300"
              : "cursor-not-allowed"
          }`}
          onClick={activateEditButton ? handleClickOptions : () => {}}
          title={
            activateEditButton
              ? "More options"
              : "Not allowed to edit this user"
          }
        >
          <MdMoreHoriz
            className={`w-6 h-6 ${
              activateEditButton ? "fill-neutral-700" : "fill-neutral-200"
            }`}
          />
        </button>
        <EditDeleteMenu
          show={showOptionsMenu}
          includeEdit={includeEdit}
          includeDelete={includeDelete}
          handleEdit={handleClickOptionWrapper(handleCardClick)}
          handleDelete={handleClickOptionWrapper(handleDelete)}
          containerStyle="absolute w-fit top-[64px] right-0 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
        />
      </div>
    </div>
  );
};

export default StudentCard;
